import React, {useMemo} from "react";
import { PersonAndBoxMessage } from "../../../components/messages/PersonAndBoxMessage";
import { useSnackbarDisplayOnError } from "../../../hooks/error/useSnackbarDisplayOnError";
import { useBindSearchQuery } from "../../../hooks/useBindSearchQuery";
import { useGetBoundReportPaginated } from "../../../hooks/request/useGetBoundReportPaginated";
import { BoundTablePagination } from "../../../components/common/inputs/boundInputs";
import ReportDataSummary from "../../../components/report/redesigned/ReportDataSummary";
import { LocationFavoriteSearchViewVO } from "../../../types/model/valueObject/location/LocationFavoriteSearchViewVO";
import { BuyerLocationFavoriteSortFilter, LocationFavoriteSearchParams } from "../../../components/buyer/locationFavorite/BuyerLocationFavoriteSortFilter";
import { BuyerLocationFavoriteCardListWithLoader } from "../../../components/buyer/locationFavorite/BuyerLocationFavoriteCardList";
import { locationRequests } from "../../../requests/api-endpoints";
import {Grid} from "@mui/material";
import {useSnackbar} from "../../../context/snackbar-context";

// a default query MUST be defined outside of any components to avoid re-initialization on each render
const DEFAULT_QUERY: LocationFavoriteSearchParams = {
  city: "",
  state: "",
  equipmentType: "",
  sortBy: "LOCATION_NAME",
  sortDir: 'asc',
  page: 0,
  size: 10,
};

const LocationsBuyerFavorites = () => {
  // state hooks
  const [reportQuery, setReportQuery] = useBindSearchQuery(DEFAULT_QUERY);

  const { snackbarShowMessage } = useSnackbar();

  // Api hooks
  const {
    reportPayload,
    pageInfo,
    isLoading,
    error
  } =  useGetBoundReportPaginated<LocationFavoriteSearchViewVO[]>({
    requestUrl: locationRequests.searchFavorites.search,
    requestParams: reportQuery
  });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && reportPayload?.length === 0
  }, [error, reportPayload]);

  const showPagination = useMemo(() => {
    return !!(reportPayload?.length && reportPayload.length > 0);
  }, [reportPayload])

  // change handlers
  const handleFilterChange = (newQuery: LocationFavoriteSearchParams) => {
    const updatedQuery = {
      ...reportQuery,
      ...newQuery
    };
    if (!newQuery.page) {
      // reset to first page on filter change if it is not being explicitly set
      updatedQuery.page = 0;
    }

    setReportQuery(updatedQuery);
  }

  return (
    <Grid item container>
      <BuyerLocationFavoriteSortFilter
        query={reportQuery}
        onChange={handleFilterChange}
      />
      <ReportDataSummary
        visible={!shouldShowPersonAndBox}
        count={pageInfo?.count || 0}
        title={"All"}
        isShowCollapseButton={false}
      />
      <BuyerLocationFavoriteCardListWithLoader
        visible={!error}
        isLoading={isLoading}
        items={reportPayload}
      />
      <PersonAndBoxMessage
        visible={shouldShowPersonAndBox }
      />
      <BoundTablePagination<LocationFavoriteSearchParams>
        visible={showPagination}
        pageInfo={pageInfo}
        onChange={handleFilterChange}
      />
    </Grid>
  );
};

export default LocationsBuyerFavorites;
